import { produce } from 'immer';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  setValidationErrors,
  showErrorToast,
  showSuccessToast,
} from '../../../../../../common/global/helpers';
import Button from '../../../../../../common/widgets/Button';
import { ErrorPanel } from '../../../../../../common/widgets/ErrorPanel';
import Input from '../../../../../../common/widgets/Input';
import LoadingSpinner from '../../../../../../common/widgets/LoadingSpinner';
import { ErrorState, ErrorType } from '../../../../../benefit/models/Errors';
import { AccountInfoContext } from '../../../../contexts/AccountInfoContext';
import { resources } from '../../../../resources/resources';
import { merchantAccountsService } from '../../../../services/merchant-accounts';

interface AccountDetailsForm {
  firstname?: string;
  lastname?: string;
  email?: string;
  phoneNumber?: string;
}

export const MyAccountDetails = () => {
  const { t: translate } = useTranslation();
  const [form, setForm] = useState<AccountDetailsForm>({});
  const [errors, setErrors] = useState<ErrorState>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showErrorPanel, setShowErrorPanel] = useState<boolean>(false);
  const [isSaveLoading, setIsSaveLoading] = useState<boolean>(false);
  const { accountInfo, setAccountInfo } = useContext(AccountInfoContext);

  const loadUser = useCallback(
    async (abortController?: AbortController) => {
      if (!accountInfo.id) return;
      setIsLoading(true);
      const resp = await merchantAccountsService.getEditAccountReadModel(
        accountInfo.id,
        {
          signal: abortController?.signal,
        },
      );
      if (abortController?.signal.aborted) return;
      setIsLoading(false);

      if (resp.hasErrors) {
        setShowErrorPanel(true);
      } else {
        setForm({
          firstname: resp.data.firstname,
          lastname: resp.data.lastname,
          phoneNumber: resp.data.phoneNumber,
          email: resp.data.email,
        });
      }
    },
    [accountInfo.id],
  );

  useEffect(() => {
    const abortController = new AbortController();
    loadUser(abortController);
    return () => abortController.abort();
  }, [accountInfo, loadUser]);

  const clearErrors = (field: string) => {
    setErrors(
      produce(d => {
        d[field] = [];
      }),
    );
  };

  const onSaveClick = async () => {
    setIsSaveLoading(true);
    const resp = await merchantAccountsService.updatePersonalAccount({
      firstname: form.firstname,
      lastname: form.lastname,
      phoneNumber: form.phoneNumber,
    });
    setIsSaveLoading(false);
    if (resp.hasErrors) {
      if (resp.errorType === ErrorType.ValidationError) {
        setValidationErrors(resp, setErrors);
      } else {
        showErrorToast(translate(resources.errors.generic));
      }
    } else {
      showSuccessToast(translate(resources.sveChangesConfirmation));
      setAccountInfo(
        produce(accountInfo, d => {
          d.firstName = form.firstname!;
          d.lastName = form.lastname!;
          d.fullName = `${form.firstname} ${form.lastname}`;
          d.initials = `${form.firstname!.substring(
            0,
            1,
          )} ${form.lastname!.substring(0, 1)}`;
        }),
      );
    }
  };

  if (isLoading) return <LoadingSpinner />;

  if (showErrorPanel) return <ErrorPanel />;

  return (
    <>
      <div className="row">
        <div className="col col-6">
          <Input
            label={translate(resources.accounts.myAccount.details.fistname)}
            value={form.firstname}
            onChange={e =>
              setForm(
                produce(d => {
                  d.firstname = e.value;
                }),
              )
            }
            errors={errors.firstname}
            clearErrors={() => clearErrors('firstname')}
            mandatory
          />
        </div>
        <div className="col col-6">
          <Input
            label={translate(resources.accounts.myAccount.details.lastname)}
            value={form.lastname}
            onChange={e =>
              setForm(
                produce(d => {
                  d.lastname = e.value;
                }),
              )
            }
            errors={errors.lastname}
            clearErrors={() => clearErrors('lastname')}
            mandatory
          />
        </div>
      </div>
      <div className="row">
        <div className="col col-6">
          <Input
            label={translate(resources.accounts.myAccount.details.email)}
            value={form.email}
            disabled
          />
        </div>
        <div className="col col-6">
          <Input
            label={translate(resources.accounts.myAccount.details.phoneNumber)}
            value={form.phoneNumber}
            onChange={e =>
              setForm(
                produce(d => {
                  d.phoneNumber = e.value;
                }),
              )
            }
            errors={errors.phoneNumber}
            clearErrors={() => clearErrors('phoneNumber')}
            mandatory
          />
        </div>
      </div>
      <div className="m-panel-actions-bottom">
        <Button
          className="btn btn-solid"
          onClick={onSaveClick}
          loading={isSaveLoading}
        >
          <span className="btn-text">{translate(resources.save)}</span>
        </Button>
      </div>
    </>
  );
};
